import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { FetchPolicy } from '@apollo/client/core';
import { map, Observable, tap } from 'rxjs';
import { ApolloService } from 'src/app/services/apollo/apollo.service';
import { environment } from 'src/environments/environment';
import { QueryProjectClassificationRate } from '../components/project-classification-rates/graphql/queryProjectClassificationRates.query';
import { ProjectClassificationRateQueryVars } from '../components/project-classification-rates/interfaces/project-classification-rate-variables.interface';
import { ProjectClassificationRatesResponse } from '../components/project-classification-rates/interfaces/project-classification-rate-response.interface';
import { ProjectClassificationRateCollectionSegment } from '../components/project-classification-rates/interfaces/project-classification-rate-collection-segment.interface';
import {
  DeleteProjectRatesByIdArgs,
  DeleteProjectRatesByIdResponse,
} from '../components/project-classification-rates/interfaces/delete-project-rates-by-id.interface';
import { deleteRateFromTheProject } from '../components/project-classification-rates/graphql/deleteRateFromTheProject.mutation';
import {
  DeleteAllProjectRatesArgs,
  DeleteAllProjectRatesByIdResponse,
} from '../components/project-classification-rates/interfaces/delete-all-project-rates-vars.interface';
import { deleteAllProjectWageRatesMutation } from '../components/project-classification-rates/graphql/deleteAllRatesFromProject.mutation';
import { ProjectWageDataInformationService } from './project-wage-data-information.service';
import { LCPUserService } from 'src/app/shared/services/lcp-user/lcp-user.service';
import { DeleteProjectRateObjectResponse } from '../components/project-classification-rates/interfaces/delete-project-rates-object-response.interface';
import { DeleteProjectWageRateFactoryParams } from '../components/project-classification-rates/interfaces/delete-project-rate-factory-params.interface';
import { MasterWageRatesNotInProjectQueryVars } from '../components/project-classification-rates/interfaces/master-rates-not-in-project-variables.interface';
import { MasterWageRatesNotInProjectResponse } from '../components/project-classification-rates/interfaces/query-master-rates-not-in-project.interface';
import { QueryMasterWageRatesNotInProject } from '../components/project-classification-rates/graphql/queryMasterWageRatesNotInProject.query';
import { CreateProjectWageRateLegacyMutationResponse } from '../interfaces/create-project-wage-rate-vars.interface';
import {
  RateTemplateType,
  TrainingType,
  WageDataMutationCreateProjectWageRateLegacyArgs,
  WageDataMutationUpdateClassificationRateArgs,
  WageRateInputModelInput,
} from 'src/app/core/services/wage/wage.types';
import { ProjectWageDataFormService } from './project-wage-data-form.service';
import { ProjectFormMode } from '../enums/project-rate-form-mode.enum';
import { CreateProjectWageRateLegacyMutation } from '../components/project-classification-rates/graphql/createProjectWageRateLegacy.mutation';
import { ClassificationRatesService } from '../../../master-wage-data/services/classification-rates.service';
import UpdateMasterWageRateResponse from '../../../master-wage-data/master-wage-rates-form/interfaces/update-master-wage-rate-response.interface';
import { TrainingTypeOptions } from '../../../master-wage-data/master-wage-rates-form/enums/training-types-options.enum';
import { checkWageRateType } from '../../../master-wage-data/master-wage-rates-form/utils/checkWageRateType';

@Injectable({ providedIn: 'root' })
export class ProjectClassificationRatesService {
  private readonly _graphQlService = inject(ApolloService);
  private readonly servername = environment.graphql.wagedata.name;
  private readonly _projectClassificationRates =
    signal<ProjectClassificationRateCollectionSegment>(
      {} as ProjectClassificationRateCollectionSegment
    );
  private readonly _selectedClassificationIds = signal<string[]>([]);
  private readonly _isSelectAllChecked = signal<boolean>(false);
  private readonly _projectWageDataInformationService = inject(
    ProjectWageDataInformationService
  );
  private readonly _projectWageDataFormService = inject(
    ProjectWageDataFormService
  );
  private readonly _lcpUserService = inject(LCPUserService);
  private readonly _classificationRatesService = inject(
    ClassificationRatesService
  );
  private readonly _isCreatingNewProjectWage = computed(
    () =>
      this.currentFormMode() === ProjectFormMode.Add ||
      this.currentFormMode() === ProjectFormMode.Copy
  );
  currentFormMode = this._projectWageDataFormService.currentFormMode();
  projectWageForm = this._projectWageDataFormService.projectWageForm;

  tenantId = this._lcpUserService.getUserTenantIdValue();
  projectClassificationRates = computed(() =>
    this._projectClassificationRates()
  );
  setSelectedClassificationIds(classificationRateIds: string[]): void {
    this._selectedClassificationIds.set(classificationRateIds);
  }
  getSelectedClassificationIds(): Signal<string[]> {
    return this._selectedClassificationIds.asReadonly();
  }
  isSelectAllChecked() {
    return this._isSelectAllChecked.asReadonly();
  }
  setSelectAllCheck(isSelectAllChecked: boolean) {
    this._isSelectAllChecked.set(isSelectAllChecked);
  }

  getProjectClassificationRates(
    vars: ProjectClassificationRateQueryVars,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<boolean> {
    return this._graphQlService
      .query<ProjectClassificationRatesResponse>(
        this.servername,
        QueryProjectClassificationRate,
        vars,
        fetchPolicy
      )
      .pipe(
        tap(({ data }) => {
          this._projectClassificationRates.set(data.projectClassificationRates);
        }),
        map(() => true)
      );
  }

  getMasterWageRatesNotInProject(
    vars: MasterWageRatesNotInProjectQueryVars,
    fetchPolicy: FetchPolicy = 'network-only'
  ): Observable<boolean> {
    return this._graphQlService
      .query<MasterWageRatesNotInProjectResponse>(
        this.servername,
        QueryMasterWageRatesNotInProject,
        vars,
        fetchPolicy
      )
      .pipe(
        tap(({ data }) => {
          this._classificationRatesService.classificationRates.set(
            data.masterWageRatesNotInProject
          );
        }),
        map(() => true)
      );
  }

  deleteClassificationProjectRate(vars: DeleteProjectRatesByIdArgs) {
    return this._graphQlService
      .mutateOperation<DeleteProjectRatesByIdResponse>(
        this.servername,
        deleteRateFromTheProject,
        vars
      )
      .pipe(map(response => response?.data?.deleteProjectWageRatesLegacy!));
  }

  deleteAllProjectWageRates(vars: DeleteAllProjectRatesArgs) {
    return this._graphQlService
      .mutateOperation<DeleteAllProjectRatesByIdResponse>(
        this.servername,
        deleteAllProjectWageRatesMutation,
        vars
      )
      .pipe(map(response => response?.data?.deleteProjectWageLegacy!));
  }

  deleteProjectRateFactory(
    factoryParams: DeleteProjectWageRateFactoryParams
  ): Observable<DeleteProjectRateObjectResponse> {
    const shouldDeleteAll =
      !factoryParams.isDeletingSingleRate && this._isSelectAllChecked();

    const deleteArgs = this.buildDeleteProjectRateVars(
      factoryParams,
      shouldDeleteAll
    );

    return shouldDeleteAll
      ? this.deleteAllProjectWageRates(deleteArgs as DeleteAllProjectRatesArgs)
      : this.deleteClassificationProjectRate(
          deleteArgs as DeleteProjectRatesByIdArgs
        );
  }

  createProjectWageRate(
    vars: WageDataMutationCreateProjectWageRateLegacyArgs
  ): Observable<CreateProjectWageRateLegacyMutationResponse> {
    return this._graphQlService
      .mutateOperation<CreateProjectWageRateLegacyMutationResponse>(
        this.servername,
        CreateProjectWageRateLegacyMutation,
        vars
      )
      .pipe(map(response => response.data!));
  }

  updateProjectWageRate(
    vars: WageDataMutationUpdateClassificationRateArgs
  ): Observable<UpdateMasterWageRateResponse> {
    return this._classificationRatesService.updateMasterWage(vars);
  }

  projectWageRateFactory() {
    if (this._isCreatingNewProjectWage()) {
      const vars = this.buildCreateNewProjectWageRateVars();
      return this.createProjectWageRate(vars).pipe(
        map(data => data.createProjectWageRateLegacy)
      );
    }

    const vars = this.buildUpdateProjectWageRateVars();
    return this.updateProjectWageRate(vars).pipe(
      map(data => data.updateClassificationRate)
    );
  }

  buildCreateNewProjectWageRateVars(): WageDataMutationCreateProjectWageRateLegacyArgs {
    const { wageDeterminationInfo, craftClassifications } =
      this.projectWageForm.getRawValue();
    const trainingTypeValue =
      craftClassifications.trainingType === TrainingTypeOptions.Other
        ? null
        : (craftClassifications.trainingType as TrainingType);

    const currentProject =
      this._projectWageDataInformationService.currentProject();
    const projectId = currentProject()?.id;
    const legacyProjectId =
      this._projectWageDataInformationService.legacyProjectId();
    return {
      tenantId: this.tenantId,
      legacyProjectId: projectId ?? legacyProjectId,
      input: {
        wageRateShiftTypeId: craftClassifications.wageRateShiftTypeId,
        wageRate: this.buildWageRates(),
        wageDeterminationName: wageDeterminationInfo.wageDeterminationName!,
        modificationName:
          wageDeterminationInfo.wageDeterminationModificationName!,
        publicationDate: wageDeterminationInfo.publicationDate,
        jurisdictionLocationId: wageDeterminationInfo.jurisdictionLocationId,
        issueDate: wageDeterminationInfo.issueDate,
        expirationDate: wageDeterminationInfo.expirationDate,
        craftName: craftClassifications.craftName!,
        craftClassificationName: craftClassifications.craftClassificationName!,
        craftClassificationIsApprenticeableCraft: false,
        trainingType: trainingTypeValue,
        craftClassificationApprenticeLevel: '',
        constructionTypeId: wageDeterminationInfo.constructionTypeId!,
      },
    };
  }

  buildUpdateProjectWageRateVars(): WageDataMutationUpdateClassificationRateArgs {
    const { wageDeterminationInfo, craftClassifications } =
      this.projectWageForm.getRawValue();
    const { classificationRateId } =
      this._projectClassificationRates().items[0];
    const trainingTypeValue =
      craftClassifications.trainingType === TrainingTypeOptions.Other
        ? null
        : (craftClassifications.trainingType as TrainingType);

    return {
      id: classificationRateId,
      tenantId: this.tenantId,
      input: {
        craftClassificationName:
          craftClassifications.craftClassificationName ?? '',
        trainingType: trainingTypeValue,
        craftName: craftClassifications.craftName ?? '',
        expirationDate: wageDeterminationInfo.expirationDate ?? '',
        issueDate: wageDeterminationInfo.issueDate ?? '',
        publicationDate: wageDeterminationInfo.publicationDate ?? '',
        wageRate: this.buildWageRates(),
        wageRateShiftTypeId: craftClassifications.wageRateShiftTypeId ?? '',
      },
    };
  }

  buildWageRates(): WageRateInputModelInput {
    const { wageRates } = this.projectWageForm.getRawValue();

    const isExtended =
      this._projectWageDataFormService._rateTemplateMode() ===
      RateTemplateType.ExtendedWageRates;

    return {
      baseFringeRate: checkWageRateType(wageRates.baseFringeRate!),
      baseWageRate: checkWageRateType(wageRates.baseWageRate!),
      notes: wageRates.notes ?? '',
      standardHoursDay: checkWageRateType(wageRates.standardHoursDay!),
      totalDoubletimeRate: checkWageRateType(wageRates.totalDoubleTimeRate!),
      totalHourlyRate: checkWageRateType(wageRates.totalHourlyRate!),
      totalOvertimeRate: checkWageRateType(wageRates.totalOvertimeRate!),
      trainingRate: checkWageRateType(wageRates.totalOvertimeRate!),
      footNotes: wageRates.footNotes ?? [],
      ...(isExtended && {
        extendedWageRate: {
          doubletimeBaseRate: checkWageRateType(
            wageRates.extendedWages?.doubletimeBaseRate!
          ),
          doubletimeFringeRate: checkWageRateType(
            wageRates.extendedWages?.doubletimeFringeRate!
          ),
          firstShiftBaseRate: checkWageRateType(
            wageRates.extendedWages?.firstShiftBaseRate!
          ),
          holidayFringeRate: checkWageRateType(
            wageRates.extendedWages?.holidayFringeRate!
          ),
          overtimeBaseRate: checkWageRateType(
            wageRates.extendedWages?.overtimeBaseRate!
          ),
          overtimeFringeRate: checkWageRateType(
            wageRates.extendedWages?.overtimeFringeRate!
          ),
          saturdayBaseRate: checkWageRateType(
            wageRates.extendedWages?.saturdayBaseRate!
          ),
          saturdayFringeRate: checkWageRateType(
            wageRates.extendedWages?.saturdayFringeRate!
          ),
          standardHoursWeek: checkWageRateType(
            wageRates.extendedWages?.standardHoursWeek!
          ),
          sundayBaseRate: checkWageRateType(
            wageRates.extendedWages?.sundayBaseRate!
          ),
          sundayFringeRate: checkWageRateType(
            wageRates.extendedWages?.sundayFringeRate!
          ),
          totalSaturdayRate: checkWageRateType(
            wageRates.extendedWages?.totalSaturdayRate!
          ),
          totalSundayRate: checkWageRateType(
            wageRates.extendedWages?.totalSundayRate!
          ),
        },
      }),
    };
  }

  protected buildDeleteProjectRateVars(
    factoryParams: DeleteProjectWageRateFactoryParams,
    shouldDeleteAll: boolean
  ): DeleteProjectRatesByIdArgs | DeleteAllProjectRatesArgs {
    const currentProject =
      this._projectWageDataInformationService.currentProject();
    const projectId = currentProject()?.id;
    const tenantId = this._lcpUserService.getUserTenantIdValue();

    if (shouldDeleteAll) {
      return {
        legacyProjectId: projectId,
        tenantId,
      };
    }

    return {
      legacyProjectId: projectId,
      tenantId,
      rateIds: factoryParams.isDeletingSingleRate
        ? [factoryParams.projectClassificationRate.classificationRateId]
        : this._selectedClassificationIds(),
    };
  }
}
